import React, { useLayoutEffect, useMemo, useRef, useState } from 'react';

import type { AiAction } from '~/components/AiChat/types';
import FeedControls from '~/components/ClaimPage/CliveApp/CliveNotifications/FeedControls';
import type { StpNotification } from '~/components/ClaimPage/CliveApp/CliveNotifications/types';
import { isRunWorking } from '~/components/ClaimPage/CliveApp/conductorStatusObservation';
import ClickAwayListener from '~/components/core/Atomic/ClickAwayListener';
import Heading from '~/components/core/TextComponents/Heading';
import Text from '~/components/core/TextComponents/Text';
import Divider from '~/components/ui/atoms/Divider';
import cn from '~/Utils/cn';

import type { ActionVersionsRead } from '../ActionHistory/utils';
import type { LogPanelConversation } from '../conversation/types';
import type { ConductorAsk } from '../types';
import { logEntryElementId, scrollToLogEntry } from '../utils';

import { useFollowNewest } from './hooks/useFollowNewest';
import { useKeepReadingPosition } from './hooks/useKeepReadingPosition';
import { useLogEntries } from './hooks/useLogEntries';
import type { LogPanelStagesRail, StageChapter } from './StagesRail/types';
import { useActiveChapter } from './StagesRail/useActiveChapter';
import { useChapterOffsets } from './StagesRail/useChapterOffsets';
import AccentEntry from './AccentEntry';
import ActionEntry from './ActionEntry';
import ConversationFoot from './ConversationFoot';
import EarlierEntries from './EarlierEntries';
import HandlerTurnEntry from './HandlerTurnEntry';
import LogEntry from './LogEntry';
import QuestionEntry from './QuestionEntry';
import RequestEntry from './RequestEntry';
import RunFooter from './RunFooter';
import StagesRail from './StagesRail';
import type { LogEntryModel } from './types';
import WorkingCursor from './WorkingCursor';

/** What an entry needs from the panel rather than from its own notification. */
interface EntryContext {
  onActionUpdate: () => Promise<void>;
  onAskUpdate: () => Promise<void>;
  /** Whether the activity panel last took the reader here: from an action, a request or a question. */
  isSelected: boolean;
  onSelectActivityCard?: (key: string) => void;
}

type EntryRenderers = {
  [Kind in LogEntryModel['kind']]: (
    entry: Extract<LogEntryModel, { kind: Kind }>,
    context: EntryContext
  ) => React.ReactElement;
};

/** One renderer per kind of entry, so a new kind is a new key rather than another branch. */
const ENTRY_RENDERERS: EntryRenderers = {
  action: ({ notification, action, state, superseded }, { onActionUpdate, isSelected, onSelectActivityCard }) => (
    <ActionEntry
      notification={notification}
      action={action}
      state={state}
      superseded={superseded}
      onActionUpdate={onActionUpdate}
      isSelected={isSelected}
      onSelectActivityCard={onSelectActivityCard}
    />
  ),
  request: ({ notification, ask, state }, { onAskUpdate, isSelected, onSelectActivityCard }) => (
    <RequestEntry
      notification={notification}
      ask={ask}
      state={state}
      onAskUpdate={onAskUpdate}
      isSelected={isSelected}
      onSelectActivityCard={onSelectActivityCard}
    />
  ),
  question: ({ notification, ask, state, inResponseTo }, { onAskUpdate, isSelected, onSelectActivityCard }) => (
    <QuestionEntry
      notification={notification}
      ask={ask}
      state={state}
      onAskUpdate={onAskUpdate}
      isSelected={isSelected}
      onSelectActivityCard={onSelectActivityCard}
      inResponseTo={inResponseTo}
      onJumpToMessage={scrollToLogEntry}
    />
  ),
  handler_turn: ({ notification, action, replyEventId }) => (
    <HandlerTurnEntry
      notification={notification}
      action={action}
      onJumpToReply={replyEventId ? () => scrollToLogEntry(replyEventId) : undefined}
    />
  ),
  accent: ({ notification, accent }) => <AccentEntry notification={notification} accent={accent} />,
  log: ({ notification }) => <LogEntry notification={notification} />,
  // A run that ended on a card waiting for approval keeps the card, with the reply under it.
  run_footer: (entry, context) => (
    <div className="flex flex-col gap-16">
      {entry.ending.kind === 'action' ? renderEntry(entry.ending, context) : null}
      <RunFooter
        entry={entry}
        onAskUpdate={context.onAskUpdate}
        onJumpToMessage={scrollToLogEntry}
        onSelectActivityCard={context.onSelectActivityCard}
      />
    </div>
  ),
};

const renderEntry = (entry: LogEntryModel, context: EntryContext): React.ReactElement =>
  // The record is keyed by the same discriminant the entry carries, so the renderer picked is
  // always the one for this entry's member of the union. TypeScript cannot correlate the two
  // through an index access, which is the only reason this assertion is here.
  (ENTRY_RENDERERS[entry.kind] as (member: LogEntryModel, context: EntryContext) => React.ReactElement)(entry, context);

/**
 * What the header counts and the highlights filter keeps: Clive's actions, its requests and questions,
 * every accent card (milestone, pending, error) and Clive's replies — the entries the log draws as more
 * than a line of narration. A request or a question counts in every state, as an action does, because its
 * card is addressed to the reader whatever became of it.
 */
const HIGHLIGHT_KINDS: LogEntryModel['kind'][] = ['action', 'request', 'question', 'accent', 'run_footer'];

const isHighlight = (entry: LogEntryModel): boolean => HIGHLIGHT_KINDS.includes(entry.kind);

/** Stable, so the stages rail's read of the layout holds while there is no rail. */
const NO_STAGE_CHAPTERS: StageChapter[] = [];

/** Stable, as `NO_STAGE_CHAPTERS` is. */
const NO_CHAPTER_ENTRY = (): undefined => undefined;

interface LogPanelEntryProps extends EntryContext {
  entry: LogEntryModel;
  /** Unmarks the entry when the reader clicks away from it. */
  onDeselect: (eventId: string) => void;
}

/**
 * One entry, in the box the activity panel scrolls to. Memoized: every snapshot rebuilds the entries and hands
 * back the same object for each one it left as it was, so a live event draws its own entry and no other.
 */
const LogPanelEntryComponent: React.FC<LogPanelEntryProps> = ({
  entry,
  onActionUpdate,
  onAskUpdate,
  isSelected,
  onDeselect,
  onSelectActivityCard,
}) => {
  const eventId = entry.notification.event_id;

  return (
    // Every entry is wrapped so selecting one does not remount it; only the selected one listens.
    <ClickAwayListener
      onClickAway={(event) => {
        // Its own card's controls take the reader to this entry, so a click on one keeps it selected.
        const control = `[aria-controls="${logEntryElementId(eventId)}"]`;
        if (!(event.target instanceof Element && event.target.closest(control))) {
          onDeselect(eventId);
        }
      }}
      mouseEvent={isSelected ? 'onClick' : false}
      touchEvent={isSelected ? 'onTouchEnd' : false}
    >
      {/*
        The spacing sits on the wrapper, which is what `last:` sees; the id is what the activity panel scrolls to.
        The outline is drawn by the card itself, around the card and not the rail.

        Off screen, the browser skips an entry's layout and paint, and holds its place at the size it last drew it
        at: 80px until it has, a guess at a narration row, which most of the log is. `content-visibility: auto` also
        clips what an entry draws past its box, on screen or off, and the selected card's outline reaches past it,
        so the selected entry is always drawn. It is the one the reader was taken to, so it is on screen anyway.
      */}
      <div
        id={logEntryElementId(eventId)}
        aria-current={isSelected || undefined}
        className="mb-16 [contain-intrinsic-size:auto_80px] [content-visibility:auto] last:mb-0 aria-[current=true]:[content-visibility:visible]"
      >
        {renderEntry(entry, { onActionUpdate, onAskUpdate, isSelected, onSelectActivityCard })}
      </div>
    </ClickAwayListener>
  );
};

const LogPanelEntry = React.memo(LogPanelEntryComponent);

interface LogPanelProps {
  /** The conductor's stream, oldest first. The panel only ever reverses it, at the reader's ask. */
  notifications: StpNotification[];
  /** The claim's actions, for the entries that name one. */
  actions: AiAction[];
  /** The history read for each action Clive revised, by action id, for its entries from before the last revision. */
  actionVersions: Record<string, ActionVersionsRead>;
  /** The asks Clive raised on the claim, for the entries about one. */
  asks: ConductorAsk[];
  /** Refetches the claim's actions after a gesture, which both panes resolve from. */
  onActionUpdate: () => Promise<void>;
  /** Refetches the claim's asks after a gesture, which the log's request and question cards resolve from. */
  onAskUpdate: () => Promise<void>;
  /** The entry the activity panel last took the reader to, marked until the reader clicks outside it. */
  selectedEventId?: string;
  /** Unmarks `eventId`, if it is still the selected entry. */
  onDeselect: (eventId: string) => void;
  /** Whether there is history before the first notification that has not been read. */
  hasEarlier: boolean;
  /** Reads the page of history before the first notification into `notifications`. Rejects if the read fails. */
  onLoadEarlier: () => Promise<void>;
  /**
   * Shows a card in the activity panel, by its row's key, from the title of the entry about it.
   * Absent where the log is drawn without the panel, which leaves the titles plain.
   */
  onSelectActivityCard?: (key: string) => void;
  /**
   * The log's conversation with Clive: Clive's replies and the questions it raised in reply drawn as such, the
   * messages it has not read waiting under the log, the composer, and Clive's cursor while a run works. Absent,
   * the log is drawn as it always was.
   */
  conversation?: LogPanelConversation;
  /** The stages rail beside the log. Absent, or with no stage changes, no rail is drawn. */
  stagesRail?: LogPanelStagesRail;
}

/**
 * Left panel of `conductor_hub`: the claim's automation log, under its "Automation First" title.
 *
 * Presentational — it takes the stream and the action rows rather than reading them, so Storybook
 * can render the panel as it looks on a live claim. The tab reads both sources and passes them
 * down; an empty claim is an empty array, which is a thing the caller can say and not a default
 * this panel invents.
 *
 * The panel scrolls, so the header scrolls with it. Pinning it is a call for when there is enough
 * under it to scroll. It opens with the latest entry in view, and keeps it there on a reversal. With a
 * conversation, the log scrolls above the queue and the composer, which stay where the handler writes.
 * With the claim's stage changes, the stages rail stands beside the log, and stays put as it scrolls.
 */
const LogPanel: React.FC<LogPanelProps> = ({
  notifications,
  actions,
  actionVersions,
  asks,
  onActionUpdate,
  onAskUpdate,
  selectedEventId,
  onDeselect,
  hasEarlier,
  onLoadEarlier,
  onSelectActivityCard,
  conversation,
  stagesRail,
}) => {
  const hasConversation = conversation !== undefined;
  const status = conversation?.status ?? null;
  const isWorking = isRunWorking(status);
  // Drawn again as the status changes, and not as the queue does.
  const logConversation = useMemo(() => (hasConversation ? { status } : undefined), [hasConversation, status]);
  const entries = useLogEntries(notifications, actions, asks, actionVersions, logConversation);
  const [isReversed, setIsReversed] = useState(false);
  const [highlightsOnly, setHighlightsOnly] = useState(false);
  const [isRailExpanded, setIsRailExpanded] = useState(false);
  const scrollerRef = useRef<HTMLDivElement>(null);
  const rail = stagesRail && stagesRail.chapters.length > 0 ? stagesRail : undefined;
  const pendingChapterKey = rail?.pendingChapterKey;

  const highlights = useMemo(() => entries.filter(isHighlight), [entries]);
  const visibleEntries = useMemo(() => {
    const filtered = highlightsOnly ? highlights : entries;

    return isReversed ? [...filtered].reverse() : filtered;
  }, [entries, highlights, highlightsOnly, isReversed]);

  // The newest entry is in view on open and after every reversal: the bottom, or the top once
  // reversed.
  useLayoutEffect(() => {
    const scroller = scrollerRef.current;
    if (scroller) scroller.scrollTop = isReversed ? 0 : scroller.scrollHeight;
  }, [isReversed]);

  // Ahead of `useKeepReadingPosition`: where a page of earlier entries lands, its move is the one that stands.
  const { contentRef, isAtNewestEnd, shouldFollow } = useFollowNewest(
    scrollerRef,
    isReversed,
    visibleEntries.map(({ notification }) => notification.event_id),
    isWorking
  );

  // Reversed, the page lands below the reader and moves nothing they are reading.
  const loadEarlierInPlace = useKeepReadingPosition(
    scrollerRef,
    isReversed ? undefined : visibleEntries[0]?.notification.event_id,
    `${isReversed}:${highlightsOnly}`,
    onLoadEarlier
  );

  // A chapter older than the log takes the reader to the log's oldest end at once, where its entries will land.
  useLayoutEffect(() => {
    const scroller = scrollerRef.current;
    if (scroller && pendingChapterKey !== undefined) scroller.scrollTop = isReversed ? scroller.scrollHeight : 0;
  }, [pendingChapterKey, isReversed]);

  // After both, so it reads the place they leave the reader at.
  const activeChapterIndex = useActiveChapter(
    scrollerRef,
    visibleEntries,
    rail?.chapters ?? NO_STAGE_CHAPTERS,
    isAtNewestEnd,
    pendingChapterKey
  );
  const chapterOffsets = useChapterOffsets(
    scrollerRef,
    contentRef,
    visibleEntries,
    isReversed,
    rail?.chapters ?? NO_STAGE_CHAPTERS,
    rail?.entryIdOf ?? NO_CHAPTER_ENTRY
  );

  // At the log's oldest end, which a reversal moves to the bottom.
  const earlierEntries = (
    <EarlierEntries
      hasEarlier={hasEarlier}
      hasEntries={entries.length > 0}
      onLoadEarlier={isReversed ? onLoadEarlier : loadEarlierInPlace}
      isReadingIn={pendingChapterKey !== undefined}
    />
  );

  // At the log's newest end, as `earlierEntries` is at its oldest.
  const workingCursor = conversation ? <WorkingCursor isWorking={isWorking} /> : null;

  // Over the conversation's foot, the scrollbar's gutter is kept whether the log scrolls or not, as the foot keeps
  // it, so the two share one column. The rail stands beside the box that scrolls, not in it, and the log blurs back
  // while the rail's labels hang over it.
  const log = (
    <div className={cn('flex', conversation ? 'min-h-0 flex-1' : 'h-full')}>
      <div
        ref={scrollerRef}
        className={cn(
          'h-full min-w-0 flex-1 overflow-y-auto px-24 pt-20 transition-[filter] duration-200 motion-reduce:transition-none',
          conversation && '[scrollbar-gutter:stable]',
          isRailExpanded && 'blur-[2px]'
        )}
      >
        <div ref={contentRef}>
          <div className="flex items-center justify-between">
            <Heading variant={Heading.TYPES.H2}>Automation First</Heading>
            <FeedControls
              count={highlights.length}
              isReversed={isReversed}
              onToggleOrder={() => setIsReversed((value) => !value)}
              highlightsOnly={highlightsOnly}
              onToggleHighlights={() => setHighlightsOnly((value) => !value)}
            />
          </div>
          <Divider className="mb-16 mt-12" />
          {highlightsOnly && visibleEntries.length === 0 ? (
            <Text variant={Text.VARIANTS.SM} colorVariant={Text.COLOR_VARIANTS.SECONDARY} className="py-24 text-center">
              No notifications match the current filter.
            </Text>
          ) : null}
          {isReversed ? workingCursor : earlierEntries}
          {visibleEntries.map((entry) => (
            <LogPanelEntry
              key={entry.notification.event_id}
              entry={entry}
              onActionUpdate={onActionUpdate}
              onAskUpdate={onAskUpdate}
              isSelected={entry.notification.event_id === selectedEventId}
              onDeselect={onDeselect}
              onSelectActivityCard={onSelectActivityCard}
            />
          ))}
          {isReversed ? earlierEntries : workingCursor}
        </div>
      </div>
      {rail ? (
        <StagesRail
          chapters={rail.chapters}
          offsets={chapterOffsets}
          isReversed={isReversed}
          activeIndex={activeChapterIndex}
          isExpanded={isRailExpanded}
          onExpandedChange={setIsRailExpanded}
          onSelectChapter={rail.onSelectChapter}
        />
      ) : null}
    </div>
  );

  if (!conversation) return log;

  return (
    <div className="flex h-full flex-col">
      {log}
      {/* Under the rail too, the composer's edges are the entries'. */}
      <div className={cn('shrink-0', rail && 'pr-40')}>
        <ConversationFoot
          conversation={conversation}
          entries={entries}
          scrollerRef={scrollerRef}
          isReversed={isReversed}
          shouldFollow={shouldFollow}
        />
      </div>
    </div>
  );
};

export default LogPanel;
